import BN from "bn.js";
import { omit } from "lodash";
import {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { noop } from "rxjs";
import { Option } from "space-monad";
import { useBrowserWallet } from "../../../../../store/wallets/crypto";
import { StakeContext } from "../../context";
import { denominator } from "../common";

const errors = {
  valid: "Value is not valid",
  balance: "You do not have enough credit",
  min: "Staking amount is too low",
  max: "Staking amount is too high",
};

export function useStakingForm(defaultValue: Option<BN>) {
  const { currentContract, decimals } = useContext(StakeContext);

  const [fieldValue, setFieldValue] = useState<string>(() =>
    defaultValue.map((x) => x.div(denominator).toString()).getOrElse("100000")
  );
  const [bnAmount, setBnAmount] = useState<BN>(new BN(0));
  const [internalError, setInternalError] = useState<Partial<typeof errors>>(
    {}
  );
  const [submitted, setSubmitted] = useState<boolean>(false);
  const { balance } = useBrowserWallet();

  const clearError = useCallback(
    (type: keyof typeof errors) => {
      setInternalError(omit(internalError, type));
    },
    [internalError]
  );

  const addError = useCallback(
    (type: keyof typeof errors) => {
      setInternalError({ ...internalError, [type]: errors[type] });
    },
    [internalError]
  );

  const hasError = useCallback(
    (type: keyof typeof errors) => {
      return internalError[type] !== undefined;
    },
    [internalError]
  );

  // Error on number validity
  useEffect(() => {
    try {
      const newVal = new BN(
        fieldValue + Array(decimals.getOrElse(0) + 1).join("0")
      );
      setBnAmount(newVal);
      clearError("valid");
    } catch (e: any) {
      addError("valid");
      // do nsot update bnAmount
    }
  }, [fieldValue, decimals]);

  // Error for not enough balance
  useEffect(() => {
    balance.fold(noop, (balance) => {
      if (!hasError("balance") && bnAmount.gte(balance.amount)) {
        addError("balance");
      } else if (hasError("balance") && !bnAmount.gt(balance.amount)) {
        clearError("balance");
      }
    });
  }, [bnAmount, balance]);

  useEffect(() => {
    currentContract
      .map((x) => x.walletInfo)
      .fold(noop, ({ minStake, maxStake, poolSize, totalStakedTokens }) => {
        // Error for min stake
        if (!hasError("min") && minStake.gt(bnAmount)) {
          addError("min");
        } else if (hasError("min") && bnAmount.gte(minStake)) {
          clearError("min");
        }

        // Error for max stake
        if (
          !hasError("max") &&
          (maxStake.lt(bnAmount) ||
            bnAmount.add(totalStakedTokens).gt(poolSize))
        ) {
          addError("max");
        } else if (
          hasError("max") &&
          (bnAmount.lte(maxStake) ||
            poolSize.gt(bnAmount.add(totalStakedTokens)))
        ) {
          clearError("max");
        }
      });
  }, [bnAmount, currentContract]);

  const handleSubmit = useCallback(
    (fn: (x: BN) => Promise<void> | void) => async (e?: SyntheticEvent) => {
      if (e) e.preventDefault();
      setSubmitted(true);
      if (Object.keys(internalError).length < 1) {
        return await fn(bnAmount);
      }
      throw new Error("Form has errors");
    },
    [setSubmitted, internalError]
  );

  const error = useMemo(
    () =>
      submitted
        ? internalError[
            Object.keys(internalError)[0] as keyof typeof internalError
          ]
        : undefined,
    [submitted, internalError]
  );

  return {
    fieldValue,
    setFieldValue,
    bnAmount,
    handleSubmit,
    error,
  };
}
